import {HelperText, TextInput} from "react-native-paper"
import React, {useEffect, useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import {digs} from "diggerize"
import memo from "set-state-compare/build/memo"
import PropTypes from "prop-types"
import {useForm} from "@kaspernj/api-maker/build/form"
import useInput from "@kaspernj/api-maker/build/use-input"
import {View} from "react-native"

/**
 * @typedef {object} ComponentsAdminInputProps
 * @property {unknown} autoRows
 * @property {object} containerProps
 * @property {string} label
 * @property {number} minimumRows
 * @property {boolean} multiline
 * @property {string} name
 * @property {Function} onChangeText
 * @property {unknown} required
 * @property {object} style
 * @property {object} styles
 * @property {unknown} value
 */

/** @typedef {object} ComponentsAdminInputState */

/**
 * @extends {ShapeComponent<ComponentsAdminInputProps, ComponentsAdminInputState>}
 */
class ComponentsAdminInput extends ShapeComponent {
  static defaultProps = {
    autoRows: false,
    required: false
  }

  static propTypes = {
    autoRows: PropTypes.bool.isRequired,
    containerProps: PropTypes.object,
    label: PropTypes.string,
    minimumRows: PropTypes.number,
    multiline: PropTypes.bool,
    name: PropTypes.string,
    onChangeText: PropTypes.func,
    required: PropTypes.bool.isRequired,
    style: PropTypes.object,
    styles: PropTypes.object
  }

  setup() {
    const {inputProps, restProps: restPropsFromUseInput, wrapperOpts} = useInput({props: this.props, wrapperOptions: {type: "input"}})
    const {name} = inputProps
    const {errors} = digs(wrapperOpts, "errors")

    this.errors = errors
    this.inputProps = inputProps
    this.restPropsFromUseInput = restPropsFromUseInput
    this.wrapperOpts = wrapperOpts
    this.form = useForm()
    const hasControlledValue = "value" in this.props
    const rawInitialValue = hasControlledValue ? inputProps.value : inputProps.defaultValue
    const initialValue = inputProps.type == "checkbox" || rawInitialValue !== null ? rawInitialValue : ""

    // Set initial value in form
    useMemo(() => {
      if (this.form && name && typeof initialValue != "undefined") {
        this.form.setValue(name, initialValue)
      }
    }, [])

    useEffect(() => () => {
      if (this.form && name) {
        this.form.unsetValue(name)
      }
    }, [this.form, name])
  }

  render() {
    const {errors, inputProps, restPropsFromUseInput, wrapperOpts} = this.tt
    const {
      autoRows,
      attribute,
      containerProps,
      dataSet,
      defaultValue,
      label,
      minimumRows,
      model,
      name,
      onChangeText,
      required,
      style,
      styles,
      value,
      ...restProps
    } = restPropsFromUseInput
    const {
      ref: inputRef,
      defaultValue: inputDefaultValue,
      value: _inputValue,
      ...inputPropsWithoutRef
    } = inputProps
    const actualStyle = this.stylingFor("textInput", Object.assign(
      {},
      style
    ))
    const textInputExtraProps = {}
    const shouldControlValue = "value" in this.props
    const mergedDataSet = this.cache(
      "textInputDataSet",
      Object.assign({id: inputProps.id}, dataSet || {}),
      [inputProps.id, dataSet]
    )
    const containerDataSet = this.cache(
      "containerDataSet",
      containerProps?.dataSet || {},
      [containerProps?.dataSet]
    )

    if (autoRows && this.props.multiline && !("rows" in this.props)) {
      // If no rows prop is passed, calculate the number of rows based on the value
      const currentValue = shouldControlValue ? this.props.value : inputDefaultValue
      let rows = currentValue ? currentValue.split("\n").length : 1

      if (minimumRows && minimumRows > rows) {
        rows = minimumRows
      }

      if (!("whiteSpace" in actualStyle)) {
        actualStyle.whiteSpace = "nowrap"
      }

      textInputExtraProps.rows = rows
    }

    return (
      <View {...containerProps} dataSet={containerDataSet}>
        <TextInput
          {...inputPropsWithoutRef}
          {...(shouldControlValue ? {value: this.props.value || ""} : {defaultValue: inputDefaultValue ?? ""})}
          dataSet={mergedDataSet}
          error={errors.length > 0}
          label={`${wrapperOpts.label}${required ? "*" : ""}`}
          onChangeText={this.tt.onChangeText}
          placeholderTextColor="#b0b0b0"
          ref={inputRef}
          style={actualStyle}
          {...textInputExtraProps}
          {...restProps}
        />
        {errors.length > 0 &&
          <HelperText type="error">
            {this.errorMessages().join(". ")}
          </HelperText>
        }
      </View>
    )
  }

  errorMessages() {
    const {errors} = this.tt
    const errorMessages = []

    for (const error of errors) {
      for (const errorMessage of error.getErrorMessages()) {
        errorMessages.push(errorMessage)
      }
    }

    return errorMessages
  }

  onChangeText = (value) => {
    const {form, inputProps} = this.tt
    const {onChangeText} = this.props
    const {name} = inputProps

    if (form && name) {
      form.setValue(name, value)
    }

    if (onChangeText) {
      onChangeText(value)
    }
  }
}

export default memo(shapeComponent(ComponentsAdminInput))
